import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import langReducer from './features/lang/langSlice';
import trayReducer from './features/tray/traySlice';
import jobsReducer from './features/jobs/jobsSlice';
import recentSearchesReducer from './features/savedSearchesSlice';
import comparisonPreferencesReducer, { setAllWeights } from './features/comparisonPreferencesSlice';
import jobFormReducer, { setField } from './features/jobFormSlice';
import uiReducer from './features/uiSlice';
import { sentryMiddleware } from './sentryMiddleware';
import { userStateReducer, hydrateFromSSR } from './features/userState/userStateSlice';
import { userStateApi } from './services/userStateApi';
import type { UserStateMap } from '../types/user-state';
import { authPromptReducer } from './features/authPrompt/authPromptSlice';
import { hydrateJobsFromSSR, extractJobsFromUserState } from './features/jobs/jobsSlice';
import finlandFilterReducer from './features/finlandFilterSlice';

// Shared state for localforage instance (module-level to persist across calls)
let localForagePromise: Promise<any> | null = null;
let localForageInstance: any = null;

// Lazy loader for localforage - dynamically imports only on client side
// Vite SSR config excludes 'localforage' from server bundling
const loadLocalForage = async () => {
  if (typeof window === 'undefined') {
    return null; // Server-side: no localforage
  }
  
  if (localForageInstance) {
    return localForageInstance; // Already loaded
  }
  
  if (!localForagePromise) {
    // Start loading localforage
    localForagePromise = import('localforage').then(m => {
      // CJS/UMD interop: in some bundling modes (e.g. Vite dev serving the raw UMD file)
      // the ESM namespace has no `default` and localforage lands on `window.localforage`.
      // Without this fallback every persistence op silently no-ops.
      const instance =
        (m as { default?: unknown }).default ??
        (typeof window !== 'undefined' ? (window as { localforage?: unknown }).localforage : null);
      if (!instance) {
        console.error('[createStore] localforage loaded without a usable export — persistence disabled');
        return null;
      }
      localForageInstance = instance;
      return localForageInstance;
    }).catch((err) => {
      console.error('[createStore] Failed to load localforage:', err);
      localForagePromise = null; // Reset on error so we can retry
      return null;
    });
  }
  
  return localForagePromise;
};

// Pre-load localforage on client side as soon as module loads
if (typeof window !== 'undefined') {
  loadLocalForage().catch(() => {
    // Silently fail - will use no-op storage
  });
}

// Getter function that returns localforage storage adapter
// This returns a storage-like object that works with redux-persist
const getLocalForage = () => {
  if (typeof window === 'undefined') {
    // Server-side: return a no-op storage object
    return {
      getItem: () => Promise.resolve(null),
      setItem: () => Promise.resolve(null),
      removeItem: () => Promise.resolve(null),
      clear: () => Promise.resolve(null),
    };
  }
  
  // Client-side: return a storage adapter that uses the loaded instance
  // The methods handle the case where localforage isn't loaded yet
  return {
    getItem: (key: string) => {
      if (localForageInstance) {
        return localForageInstance.getItem(key);
      }
      // If not loaded yet, wait for it to load
      return loadLocalForage().then(instance => {
        if (instance) {
          return instance.getItem(key);
        }
        return Promise.resolve(null);
      });
    },
    setItem: (key: string, value: any) => {
      if (localForageInstance) {
        return localForageInstance.setItem(key, value);
      }
      // If not loaded yet, wait for it to load
      return loadLocalForage().then(instance => {
        if (instance) {
          return instance.setItem(key, value);
        }
        return Promise.resolve(null);
      });
    },
    removeItem: (key: string) => {
      if (localForageInstance) {
        return localForageInstance.removeItem(key);
      }
      // If not loaded yet, wait for it to load
      return loadLocalForage().then(instance => {
        if (instance) {
          return instance.removeItem(key);
        }
        return Promise.resolve(null);
      });
    },
    clear: () => {
      if (localForageInstance) {
        return localForageInstance.clear();
      }
      // If not loaded yet, wait for it to load
      return loadLocalForage().then(instance => {
        if (instance) {
          return instance.clear();
        }
        return Promise.resolve(null);
      });
    },
  };
};


const rootReducer = combineReducers({
  lang: langReducer,
  tray: trayReducer,
  jobs: jobsReducer,
  recentSearches: recentSearchesReducer,
  comparisonPreferences: comparisonPreferencesReducer,
  jobForm: jobFormReducer,
  ui: uiReducer,
  userState: userStateReducer,
  authPrompt: authPromptReducer,
  finlandFilter: finlandFilterReducer,
  [userStateApi.reducerPath]: userStateApi.reducer,
});

/** Client-side singleton so SRP island and main app (e.g. saved-searches) share one store and persisted state. */
let clientStoreSingleton: ReturnType<typeof configureStore> | null = null;

/** Reset the client store singleton (for tests only). */
export function resetClientStoreSingletonForTests(): void {
  clientStoreSingleton = null;
}

/** Dispatches this call's SSR-provided userState (a job's viewed/saved status, profile, preferences, ...) into `store`. */
function applySsrStateHydration(store: { dispatch: (action: unknown) => unknown }, ssrUserState: UserStateMap): void {
  if (!ssrUserState || Object.keys(ssrUserState).length === 0) return;

  store.dispatch(hydrateFromSSR(ssrUserState));
  // Hydrate jobs from userState so saved/applied states are available for SSR
  store.dispatch(hydrateJobsFromSSR(ssrUserState));

  // Hydrate profile and preferences from userState for match score calculation
  const profileEntry = ssrUserState['profile'];
  const preferencesEntry = ssrUserState['preferences'];

  if (profileEntry?.data) {
    try {
      const profileData = profileEntry.data as any;
      // Set all profile fields from userState
      Object.keys(profileData).forEach((key) => {
        store.dispatch(setField({ field: key as any, value: profileData[key] }));
      });
    } catch (error) {
      console.warn('[createStore] Failed to hydrate profile from userState:', error);
    }
  }

  if (preferencesEntry?.data) {
    try {
      const preferencesData = preferencesEntry.data as any;
      store.dispatch(setAllWeights(preferencesData));
    } catch (error) {
      console.warn('[createStore] Failed to hydrate preferences from userState:', error);
    }
  }
}

export function createStore(pageContext: any) {
  const isServer = !pageContext.isClientSide;
  const ssrUserState: UserStateMap = pageContext.userState ?? {};
  const preloadedState = buildPreloadedState(pageContext, ssrUserState);

  // Reuse the same persisted store on the client so every caller (SRP island, job island, tools
  // pages, ...) shares one store and its persisted state — the job island used to get its own
  // throwaway store here, which meant recordViewedJob/saveJob dispatched from a job page never
  // reached localforage at all (My Jobs history always read back empty). Layer this call's own
  // SSR-provided userState (e.g. the current job's viewed/saved status) on top so it isn't lost.
  if (!isServer && typeof window !== 'undefined' && clientStoreSingleton) {
    applySsrStateHydration(clientStoreSingleton, ssrUserState);
    return clientStoreSingleton;
  }

  if (isServer) {
    // Server-side store without persistence
    const store = configureStore({
      reducer: rootReducer,
      preloadedState,
      devTools: false,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: false,
        }).concat(sentryMiddleware, userStateApi.middleware),
    });
    applySsrStateHydration(store, ssrUserState);
    return store;
  }

  // Client-side, first store creation this session: persisted store.
  const persistConfig = {
    key: 'root',
    storage: getLocalForage(),
    whitelist: ['lang', 'tray', 'jobs', 'recentSearches', 'comparisonPreferences', 'jobForm', 'ui', 'userState', 'finlandFilter'],
  };

  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    // Enable DevTools in both environments for consistent debugging
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(sentryMiddleware, userStateApi.middleware),
  });
  clientStoreSingleton = store;

  // `jobs`/`userState`/`jobForm`/`comparisonPreferences` are all persisted, whitelisted slices,
  // so redux-persist's async REHYDRATE wholesale-replaces them once local storage has loaded
  // (jobsSlice's own REHYDRATE handler in particular takes the persisted blob verbatim). Applying
  // this call's SSR state before that would just get clobbered a moment later — so defer it to
  // persistStore's rehydrate-complete callback and let it layer on top instead.
  persistStore(store, undefined, () => applySsrStateHydration(store, ssrUserState));

  const hasAuthenticatedUser = Boolean(pageContext?.user?.id);
  if (hasAuthenticatedUser) {
    store.dispatch(
      userStateApi.endpoints.getUserState.initiate(undefined, { forceRefetch: true })
    );
  }
  return store;
}

function buildPreloadedState(pageContext: any, ssrUserState: UserStateMap) {
  const baseState = pageContext.redux?.ssrState ? { ...pageContext.redux.ssrState } : {};
  
  // Extract jobs from userState for preloading so saved/applied states are available for SSR
  let jobsState = baseState.jobs || { viewedJobs: [], savedJobs: [], appliedJobs: [] };
  if (ssrUserState && Object.keys(ssrUserState).length > 0) {
    const extractedJobs = extractJobsFromUserState(ssrUserState);
    // Merge with any existing jobs from baseState
    const mergeJobs = (existing: any[], incoming: any[]) => {
      const existingIds = new Set(existing.map((j: any) => j.id));
      const newJobs = incoming.filter((j: any) => !existingIds.has(j.id));
      return [...existing, ...newJobs];
    };
    jobsState = {
      viewedJobs: mergeJobs(jobsState.viewedJobs || [], extractedJobs.viewedJobs),
      savedJobs: mergeJobs(jobsState.savedJobs || [], extractedJobs.savedJobs),
      appliedJobs: mergeJobs(jobsState.appliedJobs || [], extractedJobs.appliedJobs),
    };
    
    // Log summary only if there's a discrepancy between userState and extracted jobs
    const isServer = !pageContext.isClientSide;
    if (isServer) {
      const totalJobEntries = Object.keys(ssrUserState).filter(k => 
        k.startsWith('savedJob#') || k.startsWith('viewedJob#') || k.startsWith('appliedJob#')
      ).length;
      const totalExtracted = extractedJobs.savedJobs.length + extractedJobs.viewedJobs.length + extractedJobs.appliedJobs.length;
      
      if (totalJobEntries !== totalExtracted) {
        console.warn('[createStore] SSR: Job extraction mismatch:', {
          userStateJobEntries: totalJobEntries,
          extractedJobs: totalExtracted,
          saved: extractedJobs.savedJobs.length,
          viewed: extractedJobs.viewedJobs.length,
          applied: extractedJobs.appliedJobs.length,
        });
      }
    }
  }
  
  // Ensure finlandFilter is enabled by default if not in baseState
  const finlandFilterState = baseState.finlandFilter || { enabled: true };
  
  return {
    ...baseState,
    jobs: jobsState,
    userState: {
      entries: ssrUserState,
      hydratedFromSSR: true,
    },
    finlandFilter: finlandFilterState,
  };
}

export type AppStore = ReturnType<typeof createStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];